import { useCallback, useEffect, useState } from 'react';
import { createLogger } from '../../utils/logger';
import { EULA_CONFIG, PRIVACY_CONFIG } from './content';
import type { PolicyDocument } from './content';

const logger = createLogger('usePolicyDocuments');

// Module-level cache — documents are immutable for the lifetime of the page.
// Keyed by URL so that swapping REACT_APP_*_URL env vars invalidates naturally on reload.
const cache = new Map<string, string>();

interface PolicyDocumentsState {
  privacy: PolicyDocument | null;
  eula: PolicyDocument | null;
  loading: boolean;
  error: Error | null;
}

async function fetchMarkdown(url: string): Promise<string> {
  const cached = cache.get(url);
  if (cached !== undefined) return cached;

  const response = await fetch(url, { credentials: 'omit' });
  if (!response.ok) {
    throw new Error(`Failed to load ${url}: ${response.status} ${response.statusText}`);
  }
  const text = await response.text();
  cache.set(url, text);
  return text;
}

/**
 * Loads the Privacy Policy and EULA markdown bodies and merges them with their
 * static header config. Consumers get a fully-resolved `PolicyDocument` per doc.
 *
 * The fetch source is decoupled via `contentUrl` on each config — swap to a
 * cloud-storage URL by setting REACT_APP_PRIVACY_POLICY_URL / REACT_APP_EULA_URL.
 */
export function usePolicyDocuments(): PolicyDocumentsState & { retry: () => void } {
  const [state, setState] = useState<PolicyDocumentsState>({
    privacy: null,
    eula: null,
    loading: true,
    error: null,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    Promise.all([fetchMarkdown(PRIVACY_CONFIG.contentUrl), fetchMarkdown(EULA_CONFIG.contentUrl)])
      .then(([privacyContent, eulaContent]) => {
        if (cancelled) return;
        setState({
          privacy: { ...PRIVACY_CONFIG, content: privacyContent },
          eula: { ...EULA_CONFIG, content: eulaContent },
          loading: false,
          error: null,
        });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        logger.error('Failed to load policy documents:', err);
        setState({ privacy: null, eula: null, loading: false, error: err });
      });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => {
    cache.clear();
    setAttempt((n) => n + 1);
  }, []);

  return { ...state, retry };
}
